import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Star, Heart } from 'lucide-react'
import { specialties } from '../data/specialties'
import { carePlans } from '../data/carePlans'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { useScrollBehavior } from '../hooks/useScrollBehavior'
import Footer from '../components/Footer'
import SearchBox from '../components/SearchBox'
import FloatingSearchBox from '../components/FloatingSearchBox'

const Home = () => {
  const [activeTab, setActiveTab] = useState('specialty')
  const [favoriteCarePlans, setFavoriteCarePlans] = useState([])
  const navigate = useNavigate()

  // Scroll to top on page load
  useScrollToTop()

  // Scroll behavior for mobile
  const { isHeroVisible, showFloatingSearch } = useScrollBehavior()

  // Load favorites from localStorage
  useEffect(() => {
    const loadFavorites = () => {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      const favoritePlans = carePlans.filter(plan => favorites.includes(plan.id))
      setFavoriteCarePlans(favoritePlans)
    }

    loadFavorites()

    // Listen for storage changes to update favorites in real-time
    window.addEventListener('storage', loadFavorites)

    // Also listen for custom event when favorites change in the same tab
    window.addEventListener('favoritesChanged', loadFavorites)

    return () => {
      window.removeEventListener('storage', loadFavorites)
      window.removeEventListener('favoritesChanged', loadFavorites)
    }
  }, [])

  // Refresh favorites when tab becomes active
  useEffect(() => {
    if (activeTab === 'favorites') {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      const favoritePlans = carePlans.filter(plan => favorites.includes(plan.id))
      setFavoriteCarePlans(favoritePlans)
    }
  }, [activeTab])



  const handleSpecialtyClick = (specialtyId) => {
    navigate(`/specialty/${specialtyId}`)
  }

  const handleCarePlanClick = (carePlanId) => {
    navigate(`/care-plan/${carePlanId}`)
  }

  return (
    <div className="App">
      <section className={`hero hero-section ${!isHeroVisible ? 'hidden' : ''}`}>
        <div className="container">
          <h1>Qmed Nursing Care Plans</h1>
          <p>Comprehensive nursing care plans organized by medical specialties following the PGIE framework</p>

          <SearchBox />
        </div>
      </section>

      <div className="container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'specialty' ? 'active' : ''}`}
            onClick={() => setActiveTab('specialty')}
          >
            Specialty
          </button>
          <button
            className={`tab ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            Favorites {favoriteCarePlans.length > 0 && `(${favoriteCarePlans.length})`}
          </button>
        </div>

        <div className="content">
          {activeTab === 'specialty' && (
            <div className="specialty-grid">
              {specialties.map((specialty) => (
                <div
                  key={specialty.id}
                  className="specialty-card"
                  onClick={() => handleSpecialtyClick(specialty.id)}
                >
                  <h3>
                    <specialty.icon size={20} />
                    {specialty.name}
                  </h3>
                  <p>{specialty.description}</p>
                  <div style={{
                    position: 'absolute',
                    bottom: '1rem',
                    right: '1rem',
                    fontSize: '0.8rem',
                    color: '#00bcd4',
                    fontWeight: '600'
                  }}>
                    {specialty.count}
                  </div>
                  <ChevronRight className="chevron" size={16} />
                </div>
              ))}
            </div>
          )}

          {activeTab === 'favorites' && (
            favoriteCarePlans.length > 0 ? (
              <div className="specialty-grid">
                {favoriteCarePlans.map((carePlan) => (
                  <div
                    key={carePlan.id}
                    className="specialty-card favorites-card"
                    onClick={() => handleCarePlanClick(carePlan.id)}
                  >
                    <div className="favorites-card-header">
                      <h3>
                        <Heart size={20} />
                        {carePlan.title}
                      </h3>
                      <Star
                        className="favorite-star"
                        size={16}
                        fill="#ffc107"
                        color="#ffc107"
                      />
                    </div>
                    <p>{carePlan.description}</p>
                    <div className="favorites-card-footer">
                      <div className="specialty-category-tag">
                        {carePlan.category}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="favorites-empty">
                <Star size={48} />
                <h3>No favorites yet</h3>
                <p>Star your most-used care plans for quick access. Visit any care plan and click the star to add it to favorites.</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Floating Search Box for Mobile */}
      <FloatingSearchBox isVisible={showFloatingSearch} />

      <Footer />
    </div>
  )
}

export default Home
